import styled from '@emotion/styled';
import { useEffect, useState } from 'react';
import { theme } from '../theme';

const SwitchWrapper = styled.div`
  /* width: 80px; */
  height: 30px;
  border: 1px solid ${theme.colors.actionPrimary.default};
  border-radius: ${theme.borderRadius.m};
  display: flex;
  flex-direction: row;
  cursor: pointer;
  overflow: hidden;
`;

const SwitchItem = styled.div<{ borderLeft: boolean; animation: string; animationTime: number }>`
  flex-grow: 1;
  display: flex;
  justify-content: center;
  align-items: center;
  font-weight: 500;
  user-select: none;
  letter-spacing: 0px;
  border-left: ${({ borderLeft }) => (borderLeft ? `1px solid ${theme.colors.actionPrimary.default}` : 'none')};
  padding-left: ${({ borderLeft }) => (borderLeft ? '0px' : '2px')};
  color: ${(props) =>
    props.animation.includes('entering')
      ? theme.colors.actionInvertedPrimary.focused
      : theme.colors.actionPrimary.default};
  transition: color 0.5s;

  &:hover {
    color: ${(props) =>
      props.animation.includes('entering')
        ? theme.colors.actionInvertedPrimary.default
        : theme.colors.actionPrimary.hover};
  }

  background: linear-gradient(
    90deg,
    rgba(0, 0, 0, 0) 0%,
    rgba(0, 0, 0, 0) 50%,
    ${theme.colors.actionPrimary.default} 50%
  );
  background-size: 204% 100%; /* Double the size to allow movement */
  background-position: 0% 0%;
  animation: ${(props) => props.animation} linear ${(props) => props.animationTime}s forwards;

  @keyframes entering-left {
    0% {
      background-position: 0% 0%;
    }
    100% {
      background-position: 99% 0%;
    }
  }

  @keyframes entering-right {
    0% {
      background-position: 200% 0%;
    }
    100% {
      background-position: 99% 0%;
    }
  }

  @keyframes leaving-right {
    0% {
      background-position: 99% 0%;
    }
    100% {
      background-position: 0% 0%;
    }
  }

  @keyframes leaving-left {
    0% {
      background-position: 99% 0%;
    }
    100% {
      background-position: 200% 0%;
    }
  }
`;

const switchTotalAnimationTime = 0.3;

interface ISwitchItem<T> {
  value: T;
  label: string;
}
interface SwitchProps<T> {
  value: T;
  possibleValues: ISwitchItem<T>[];
  onChange: (value: T) => void;
}

export const Switch = function <T>({ value, onChange, possibleValues }: SwitchProps<T>) {
  const [firstRender, setFirstRender] = useState(true);
  useEffect(() => {
    setTimeout(() => setFirstRender(false), 500);
  }, []);
  const [lastIndex, setLastIndex] = useState(possibleValues.findIndex((x) => x.value === value));
  const [indexOfLastHighlightedValue, setIndexOfLastHighlightedValue] = useState(
    possibleValues.findIndex((x) => x.value === value),
  );
  const indexOfLastSelectedValue = possibleValues.findIndex((x) => x.value === value);
  const directionLeft = lastIndex > indexOfLastSelectedValue;
  const direction = directionLeft ? -1 : 1;
  let highlightedIndex = Math.min(possibleValues.length - 1, Math.max(0, indexOfLastHighlightedValue + direction));
  if (indexOfLastHighlightedValue === indexOfLastSelectedValue) {
    highlightedIndex = indexOfLastSelectedValue;
  }
  let animationTime =
    lastIndex - indexOfLastSelectedValue === 0
      ? switchTotalAnimationTime
      : switchTotalAnimationTime / Math.abs(lastIndex - indexOfLastSelectedValue);
  animationTime = firstRender ? 0 : animationTime;
  useEffect(() => {
    const timeout = setTimeout(() => {
      setIndexOfLastHighlightedValue(highlightedIndex);
    }, animationTime * 1000);
    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastIndex, highlightedIndex]);
  return (
    <SwitchWrapper>
      {possibleValues.map((possibleValue, i) => {
        const shouldAnimate = i === highlightedIndex || i === indexOfLastHighlightedValue;
        const adjustedAnimationTime = shouldAnimate ? animationTime : 0;
        const animationName = shouldAnimate ? (i === highlightedIndex ? 'entering' : 'leaving') : 'none';
        const animationNameWithDirection = shouldAnimate
          ? `${animationName}-${directionLeft ? 'left' : 'right'}`
          : animationName;
        return (
          <SwitchItem
            key={i}
            animation={animationNameWithDirection}
            animationTime={adjustedAnimationTime}
            borderLeft={
              i > 0 &&
              !(
                i !== highlightedIndex &&
                indexOfLastHighlightedValue === indexOfLastSelectedValue &&
                i === indexOfLastSelectedValue
              )
            }
            onClick={() => {
              if (value === possibleValue.value) return;
              setLastIndex(indexOfLastSelectedValue);
              onChange(possibleValue.value);
            }}
          >
            {possibleValue.label}
          </SwitchItem>
        );
      })}
    </SwitchWrapper>
  );
};
